import { quantityTotals } from "../../../shared/production/quantity";
import type { ProductionRun, SupervisorIssueRecord } from "./models";
import { isPassivePlanRun } from "./MachinePlanFlow";
import { comparePlannedRuns, needsCarryoverDecision, operationalShiftAt, planIntersects, runsForTerminalWeek } from "../../../shared/production/plan-window";

export const elapsedMinutes = (value: string, now: number) => Math.max(0, Math.floor((now - Date.parse(value)) / 60000));
export const producedQuantity = (run: ProductionRun) => quantityTotals(run.entries, run.unit).production;
export const producedQuantityInWindow = (run: ProductionRun, start: number, end: number) => quantityTotals(run.entries.filter((entry) => {
  const time = Date.parse(entry.occurredAt); return Number.isFinite(time) && time >= start && time < end;
}), run.unit).production;

export function producedQuantityWithImportBaseline(run: ProductionRun, recordedProduced: number | null) {
  if (recordedProduced === null) return null;
  if (run.planFlowKey && run.planProducedAtImport == null) return null;
  return (run.planProducedAtImport ?? 0) + recordedProduced;
}

export function supervisorOutputQuantities(run: ProductionRun, produced: number | null, shiftProduced: number | null) {
  const fallback = { produced, planned: run.plannedQuantity, shiftProduced, unit: run.unit, boxesPerCase: null };
  const counter = run.localPlc;
  if (run.unit.trim().toLocaleUpperCase("tr-TR") !== "KOLİ" || !counter ||
      !Number.isSafeInteger(counter.boxes) || counter.boxes < 0 ||
      !Number.isSafeInteger(counter.boxesPerPlanUnit) || counter.boxesPerPlanUnit < 1 ||
      produced === null || shiftProduced === null || !Number.isFinite(produced) || !Number.isFinite(shiftProduced) ||
      Math.round(produced * counter.boxesPerPlanUnit) !== counter.boxes) return fallback;
  const planned = run.plannedQuantity == null ? null : run.plannedQuantity * counter.boxesPerPlanUnit;
  const shiftBoxes = Math.round(shiftProduced * counter.boxesPerPlanUnit);
  if ((planned != null && !Number.isSafeInteger(planned)) || !Number.isSafeInteger(shiftBoxes)) return fallback;
  return { produced: counter.boxes, planned, shiftProduced: shiftBoxes, unit: "kutu", boxesPerCase: counter.boxesPerPlanUnit };
}

export type SupervisorRunStateCode = "CRITICAL_STOP" | "SUPERVISOR_DECISION" | "TERMINAL_OPERATOR" | "DELAYED_START" | "PRODUCTION" | "WAITING_PLAN" | "PLANNED_ACTIVITY" | "PLANNED_CLOSED" | "ON_HOLD" | "COMPLETED" | "CANCELLED" | "NO_ACTIVE_PLAN";
export type SupervisorRunState = { code: SupervisorRunStateCode; label: string; priority: number; tone: "critical" | "decision" | "technical" | "working" | "planned" | "neutral"; needsDecision: boolean };

const issueFromValues = (value: unknown): SupervisorIssueRecord | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const issue = value as Partial<SupervisorIssueRecord>;
  if (!issue.id || !issue.category || !issue.description || !issue.startedAt || !issue.source || !issue.status) return null;
  return issue as SupervisorIssueRecord;
};

export function currentSupervisorIssue(run: ProductionRun) {
  for (const decision of run.decisions) {
    const issue = decision.issue ?? issueFromValues(decision.newValues?.supervisorIssue);
    if (issue) return issue;
  }
  return null;
}

export function supervisorRunState(run: ProductionRun, now = Date.now()): SupervisorRunState {
  if (run.disposition === "CANCELLED") return { code:"CANCELLED", label:"İptal edildi", priority:9, tone:"neutral", needsDecision:false };
  if (run.status === "COMPLETED") return { code:"COMPLETED", label:"Tamamlandı", priority:8, tone:"neutral", needsDecision:false };
  if (run.planItemKind === "ACTIVITY") {
    const closed = /KAPALI|BAKIM|TEMİZLİK|MOLA/u.test(run.productName.toLocaleUpperCase("tr-TR"));
    return { code:closed ? "PLANNED_CLOSED" : "PLANNED_ACTIVITY", label:closed ? "Planlı kapalı" : "Planlı faaliyette", priority:closed ? 7 : 6, tone:"planned", needsDecision:false };
  }
  const downtime = run.status === "ACTIVE" ? run.downtimes.find(item => !item.endedAt) : undefined;
  if (downtime) return { code:"CRITICAL_STOP", label:"Plansız duruşta", priority:0, tone:"critical", needsDecision:true };
  const issue = currentSupervisorIssue(run);
  if (issue && issue.status !== "RESOLVED") return { code:"SUPERVISOR_DECISION", label:issue.status === "REVIEWED" ? "Amir kararı bekliyor" : "Aksiyon alındı", priority:1, tone:"decision", needsDecision:true };
  if (needsCarryoverDecision(run, now)) return { code:"SUPERVISOR_DECISION", label:"Amir kararı bekliyor", priority:1, tone:"decision", needsDecision:true };
  if (!run.assignedOperatorId && run.status !== "ACTIVE") return { code:"TERMINAL_OPERATOR", label:"Operatör ataması yok", priority:2, tone:"technical", needsDecision:true };
  if (run.disposition === "ON_HOLD") return { code:"ON_HOLD", label:"Beklemeye alındı", priority:1, tone:"decision", needsDecision:true };
  if (run.status === "ACTIVE") return { code:"PRODUCTION", label:"Üretimde", priority:4, tone:"working", needsDecision:false };
  if (!run.plannedStartAt) return { code:"WAITING_PLAN", label:"Başlamaya hazır", priority:5, tone:"neutral", needsDecision:false };
  if (run.plannedStartAt && Date.parse(run.plannedStartAt) > now) return { code:"WAITING_PLAN", label:"Plan saatini bekliyor", priority:5, tone:"neutral", needsDecision:false };
  return { code:"DELAYED_START", label:"Başlangıcı gecikti", priority:3, tone:"decision", needsDecision:true };
}

export function isActiveCarryover(run: ProductionRun, now = Date.now()) {
  const shift = operationalShiftAt(now);
  return run.status === "ACTIVE" && Boolean(run.plannedStartAt && Date.parse(run.plannedStartAt) < shift.start);
}

export function isRunRelevantToShift(run: ProductionRun, now = Date.now()) {
  const shift = operationalShiftAt(now);
  return run.status === "ACTIVE" || run.disposition === "ON_HOLD" || needsCarryoverDecision(run, now) || planIntersects(run.plannedStartAt, run.plannedEndAt, shift.start, shift.end) || (!run.plannedStartAt && run.shiftDate === shift.date && run.shiftCode === shift.shiftCode);
}

export function supervisorVisibleRunsForLine(runs: ProductionRun[], lineId: string, now = Date.now()) {
  const shift = operationalShiftAt(now);
  const production = runs.filter(run => run.lineId === lineId && run.planItemKind !== "ACTIVITY" && run.disposition !== "CANCELLED");
  const relevant = production.filter(run => isRunRelevantToShift(run, now))
    .sort((a, b) => supervisorRunState(a, now).priority - supervisorRunState(b, now).priority || comparePlannedRuns(a, b));
  if (relevant.length) return relevant;
  const next = production.filter(run => run.status === "READY" && run.plannedStartAt && Date.parse(run.plannedStartAt) >= shift.end)
    .sort(comparePlannedRuns)[0];
  return next ? [next] : [];
}

export function supervisorPlanQueues(runs: ProductionRun[], weekStart: string, weekEnd: string, now = Date.now()) {
  const weekly = [...new Set(runs.map(run => run.lineId))].flatMap(lineId => runsForTerminalWeek(runs, lineId, weekStart, weekEnd));
  const weeklyIds = new Set(weekly.map(run => run.id));
  const previous = runs.filter(run => !weeklyIds.has(run.id) && run.planItemKind !== "ACTIVITY" && run.disposition !== "CANCELLED" &&
    ((run.status === "READY" && (run.disposition === "ON_HOLD" || needsCarryoverDecision(run, now))) || run.complaints.some(item => !item.reviewedAt)));
  return { weekly, previous };
}

export function supervisorNextAction(run: ProductionRun, now = Date.now()) {
  if (run.status === "READY" && needsCarryoverDecision(run, now)) {
    return { label: "Başlatma kararı ver", action: "CONTINUE_IN_SHIFT" as const };
  }
  const issue = currentSupervisorIssue(run);
  return { label: issue && issue.status !== "RESOLVED" ? "Nedeni düzenle" : "Sorunu değerlendir", action: undefined };
}

// One work item contains all of its exceptions; reading a complaint never ends a downtime.
export function supervisorIssues(runs: ProductionRun[], now: number) {
  return runs.filter(run => run.disposition !== "CANCELLED" && !isPassivePlanRun(run)).flatMap(run => {
    const reasons: string[] = [];
    const downtime = run.status === "ACTIVE" ? run.downtimes.find(d => !d.endedAt) : undefined;
    const complaints = run.complaints.filter(c => !c.reviewedAt);
    const carryover = needsCarryoverDecision(run, now);
    const held = !carryover && run.status !== "COMPLETED" && run.disposition === "ON_HOLD";
    const overdue = !carryover && run.status === "READY" && !!run.plannedStartAt && Date.parse(run.plannedStartAt) < now;
    const overrun = run.status === "ACTIVE" && !!run.plannedEndAt && Date.parse(run.plannedEndAt) < now;
    const openIssue = currentSupervisorIssue(run);
    if (downtime) reasons.push(`${elapsedMinutes(downtime.startedAt, now)} dk duruşta`);
    if (held) reasons.push("Beklemeye alındı");
    if (complaints.length) reasons.push(`${complaints.length} açık operatör bildirimi`);
    if (carryover) reasons.push("Plan kararı bekliyor");
    if (overdue) reasons.push(`Başlangıç ${elapsedMinutes(run.plannedStartAt!, now)} dk gecikti`);
    if (overrun) reasons.push(`Plan süresi ${elapsedMinutes(run.plannedEndAt!, now)} dk aşıldı`);
    if (openIssue && openIssue.status !== "RESOLVED") reasons.push(openIssue.status === "REVIEWED" ? "Açık neden değerlendirildi" : "Aksiyon takibi açık");
    if (!reasons.length) return [];
    const note = held ? run.decisions.find(d => d.action === "PUT_ON_HOLD")?.note : undefined;
    return [{ run, reasons:[...new Set(reasons)], downtime, complaints, overdue, overrun, held, carryover, openIssue,
      priority: supervisorRunState(run, now).priority,
      detail: openIssue?.description ?? downtime?.reasonName ?? note ?? complaints[0]?.description ?? (carryover ? "İş, amir kararı verilene kadar operatör terminalinde başlatılamaz." : "Neden kayıtlı değil; sahadan teyit gerekiyor."),
      action: openIssue && openIssue.status !== "RESOLVED" ? "Nedeni düzenle" : "Sorunu değerlendir",
    }];
  }).sort((a, b) => a.priority - b.priority || (a.run.plannedStartAt ?? "").localeCompare(b.run.plannedStartAt ?? "") || a.run.lineName.localeCompare(b.run.lineName, "tr", { numeric: true }));
}
